import React, { useState, useEffect } from "react";
import { Container, Row, Col, Dropdown, Button } from "react-bootstrap";
import SearchComponent from "../../components/SearchComponent";
import NotificationIcon from "../../components/Notification";
import { FaCalendarAlt, FaPlus } from "react-icons/fa";
import { AiFillHeart } from "react-icons/ai";
import ProjectCard from "../../components/ProjectCard";
import Sidebar from "../../components/SideBar";
import axios from "axios";
import { useUser } from "../../contextProviders/userContext";
import TopNavBar from "../../components/topNavBar";
import CreateProjectModal from "../../components/CreateProjectModal";
import { API_BASE_URL } from "../../api";

// SDG options
const SDG_OPTIONS = [
  "No Poverty",
  "Zero Hunger",
  "Good Health and Well-being",
  "Quality Education",
  "Gender Equality",
  "Clean Water and Sanitation",
  "Affordable and Clean Energy",
  "Decent Work and Economic Growth",
  "Industry, Innovation and Infrastructure",
  "Reduced Inequalities",
  "Sustainable Cities and Communities",
  "Responsible Consumption and Production",
  "Climate Action",
  "Life Below Water",
  "Life on Land",
  "Peace, Justice and Strong Institutions",
  "Partnerships for the Goals",
];

const interests = [
  "Wits Food Bank",
  "Masidleni Daily Meals",
  "Wits Food Gardens",
  "Wits Student Communal Kitchen",
  "Community Food Programmes",
  "Developing Food Gardens",
  "Urban Waste Management",
  "Reading Clubs",
  "In-person tutoring",
  "Online tutoring",
  "After school assistance",
  "Health and allied assessments",
  "Sports development",
  "Arts and Cultural Development",
  "Animal welfare",
  "Befriending the elderly",
  "Clothing drive",
  "Hygiene drive",
  "Food drives",
  "Social Justice Campaigns",
  "Others",
];

const timePeriods = [
  "All Time",
  "Last Month",
  "Last 3 Months",
  "Last 6 Months",
];

const BrowseProjects = () => {
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const { user } = useUser();
  const [error, setError] = useState(null);
  const [selectedInterest, setSelectedInterest] = useState("All Interests");
  const [selectedTimePeriod, setSelectedTimePeriod] = useState("All Time");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [myProjectIds, setMyProjectIds] = useState(new Set());

  useEffect(() => {
    Promise.all([fetchMyProjects(), fetchAllProjects()]).finally(() => {
      setLoading(false);
    });
  }, []);
  const getAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const fetchMyProjects = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/participations/my-projects`,
        { headers: getAuthHeader() }
      );
      if (response.data.success) {
        const joinedIds = new Set(
          response.data.data
            .filter((project) => project.joinStatus === "approved")
            .map((project) => project._id)
        );
        setMyProjectIds(joinedIds);
      }
    } catch (error) {
      console.error("Error fetching my projects:", error);
    }
  };

  const fetchAllProjects = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/projects`,
        {
          headers: getAuthHeader(),
        }
      );
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Failed to fetch projects. Please try again later.");
    }
  };

  const handleJoinProject = async (projectId, result) => {
    alert(result?.message || "Successfully joined the project!");
    window.location.reload();
  };

  const handleInterestSelect = (interest) => setSelectedInterest(interest);
  const handleTimePeriodSelect = (period) => setSelectedTimePeriod(period);
  const handleSearchChange = (term) => setSearchTerm(term);

  // Filtering logic
  const filteredProjects = projects
    .filter((project) => project.approved === true)
    .filter((project) => !myProjectIds.has(project._id)) // Filter out projects user has already joined
    .filter((project) => {
      if (selectedInterest !== "All Interests") {
        return project.categories?.includes(selectedInterest);
      }
      return true;
    })
    .filter((project) => {
      if (selectedTimePeriod !== "All Time") {
        const now = new Date();
        let cutoffDate = new Date();
        switch (selectedTimePeriod) {
          case "Last Month":
            cutoffDate.setMonth(now.getMonth() - 1);
            break;
          case "Last 3 Months":
            cutoffDate.setMonth(now.getMonth() - 3);
            break;
          case "Last 6 Months":
            cutoffDate.setMonth(now.getMonth() - 6);
            break;
          default:
            break;
        }
        return new Date(project.startDate) >= cutoffDate;
      }
      return true;
    })
    .filter((project) => {
      if (searchTerm.trim() !== "") {
        const searchLower = searchTerm.toLowerCase();
        return (
          project.name.toLowerCase().includes(searchLower) ||
          project.description.toLowerCase().includes(searchLower) ||
          project.organiserName.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="d-flex flex-column flex-lg-row">
      <Sidebar />

      <Container fluid className="py-2 mt-3">
        <TopNavBar />

        {/* Header Section */}
        <Row className="align-items-center justify-content-between px-3 g-2">
          <Col xs={12} md={6}>
            <div className="d-flex flex-column text-start">
              <h4 className="mb-0 fw-bold" style={{ color: "var(--color-text)" }}>
                Hi, {user.name}
              </h4>
              <small style={{ color: "var(--color-text-muted)" }}>
                Browse through Events and Projects
              </small>
            </div>
          </Col>
          <Col
            xs={12}
            md={6}
            className="d-flex align-items-center justify-content-start justify-content-md-end mt-2 mt-md-0">
            <div className="d-flex align-items-center gap-2 w-100 justify-content-end">
              <SearchComponent onSearchChange={handleSearchChange} />
              <NotificationIcon />
            </div>
          </Col>
        </Row>

        <hr style={{ color: "var(--color-border)", width: "100%" }} />

        {/* Filters Section */}
        <Row className="mt-3 align-items-center px-3 g-2">
          <Col xs={12} md={6} className="mb-2 mb-md-0">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Dropdown onSelect={handleTimePeriodSelect}>
                <Dropdown.Toggle
                  variant="light"
                  style={{ background: "var(--color-border)", height: "38px" }}>
                  <FaCalendarAlt className="me-1" />
                  {selectedTimePeriod}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {timePeriods.map((period) => (
                    <Dropdown.Item key={period} eventKey={period}>
                      {period}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown onSelect={handleInterestSelect}>
                <Dropdown.Toggle
                  variant="light"
                  style={{ background: "var(--color-border)", height: "38px" }}>
                  <AiFillHeart className="me-1" />
                  {selectedInterest}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{ maxHeight: "200px", overflowY: "auto" }}>
                  <Dropdown.Item eventKey="All Interests">
                    All Interests
                  </Dropdown.Item>
                  {interests.map((interest) => (
                    <Dropdown.Item key={interest} eventKey={interest}>
                      {interest}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Col>

          <Col xs={12} md={6} className="d-flex justify-content-start justify-content-md-end">
            <Button
              className="d-flex align-items-center justify-content-center"
              style={{ backgroundColor: "var(--color-primary)", borderColor: "var(--color-primary)" }}
              onClick={() => setShowModal(true)}>
              <FaPlus className="me-1" />
              <span>Add Project</span>
            </Button>
          </Col>
        </Row>

        {/* Projects Grid */}
        <Row xs={1} sm={2} md={3} lg={3} className="g-3 mt-4">
          {filteredProjects.length > 0 ? (
            filteredProjects.map((project) => (
              <Col key={project._id}>
                <ProjectCard
                  project={project}
                  isJoined={project.joinStatus === "approved"}
                  onJoin={(result) => handleJoinProject(project._id, result)}
                  isFlippable={true}
                />
              </Col>
            ))
          ) : (
            <Col>
              <p>No projects found matching the selected filters.</p>
            </Col>
          )}
        </Row>

        {/* Create Project Modal */}
        <CreateProjectModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onProjectCreated={(newProject) => {
            setProjects([...projects, newProject]);
            // setShowModal(false);
          }}
          interests={interests}
          sdgOptions={SDG_OPTIONS}
        />
      </Container>
    </div>
  );
};

export default BrowseProjects;
